import type { DataTableFilterField } from '@/components/data-table/types'
import { field, createSchema } from '@/lib/store/schema'
import type { PatientRecord } from '../types'

/**
 * OpenStatus filter fields for the Patients v3 table. Scoped to the visible
 * columns; `source_id` is intentionally NOT filterable (deferred — lowest-value
 * + PHI oracle-leak surface). Used by DataTableFilterControls (sidebar) and
 * the filter sidebar.
 *
 * `patient_name` is a derived/synthetic column id (the concat of First/Middle/
 * Last, filtered server-side), not a PatientRecord field — hence the cast.
 */
export const patientsFilterFields: DataTableFilterField<PatientRecord>[] = [
  {
    label: 'Client Name',
    value: 'patient_name' as keyof PatientRecord,
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Date of Birth',
    value: 'date_of_birth',
    type: 'timerange',
    defaultOpen: true,
  },
  {
    label: 'Phone',
    value: 'phone',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Primary Insurance',
    value: 'primary_insurance',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Active',
    value: 'is_active',
    type: 'checkbox',
    defaultOpen: true,
    options: [
      { label: 'Active', value: 'true' },
      { label: 'Inactive', value: 'false' },
    ],
  },
]

/**
 * BYOS schema for URL serialization.
 * input -> string; timerange -> array(timestamp); checkbox -> array(string).
 */
export const patientsSchema = createSchema({
  // Deep-link only — NOT in patientsFilterFields, so it never renders as a
  // filter chip. The call sidesheet's client-name link carries
  // `?patients.id=<uuid>&details=1`; PatientsList reads it for the initial row
  // selection and an exact-id query filter that narrows the table to one client.
  id: field.string().default(null),
  patient_name: field.string().default(null),
  // NB: default ',' delimiter, NOT '-'. DOB is commonly pre-1970 → a negative
  // epoch whose leading '-' would collide with a '-' delimiter and corrupt the
  // URL round-trip (e.g. 1955 → 1984).
  date_of_birth: field.array(field.timestamp()).default([]),
  phone: field.string().default(null),
  primary_insurance: field.string().default(null),
  is_active: field.array(field.string()).delimiter(',').default([]),
})

export type PatientsFilterState = typeof patientsSchema._type

/**
 * The deep link that opens the Clients table narrowed to one client with its
 * details sheet open — the `patients.id` + `details` shape PatientsList reads.
 */
export function patientDetailsHref(patientId: string): string {
  return `/data/patients?patients.id=${encodeURIComponent(patientId)}&details=1`
}
